/**
 * RestructureBundleDialog — Phase 36.1 rebuild.
 *
 * Cross-group membership picker (all active recurring memberships across groups),
 * per-item deposit disposition selects with explicit Held-deposit pairing,
 * a credit disposition select (paid-gated: Refund disabled when overlap is unpaid),
 * a refund payment section (Cash/UPI + date + optional UPI proof),
 * and a live net-due preview matching the server formula.
 *
 * Props change from Phase 36:
 *   REMOVED: quoteGroupId, memberships
 *   ADDED:   allActiveMemberships, heldDeposits
 *   KEPT:    accountId, periods, open, onOpenChange
 *
 * Submit sends: membershipIds + items + depositDispositions + creditDisposition
 *   + refund fields via useRestructureBundle.mutateAsync. quoteGroupId is NOT sent.
 *
 * Uses plain <Label>+markup for display-only fields (no FormField ancestor needed —
 * feedback_formlabel_needs_formfield; FormLabel without FormField → white screen).
 *
 * Plan 36.1-05, Tasks 1 + 2.
 *
 * Net-due formula (mirrors server, RESEARCH Code Example A):
 *   netDue = effectivePackageTotal
 *           − (creditDisposition === "adjust" ? creditTotalForPreview : 0)
 *           − sum(pairedDepositAmount where depositDisposition === "adjust_package")
 */

import { useState } from "react";
import {
  useRestructureBundle,
  type Membership,
  type PlanSchedulePeriod,
  type RestructureResult,
  type RestructureItem,
  type DepositDisposition,
  DepositDispositionDisposition,
  RestructureBodyCreditDisposition,
  RestructureBodyRefundPaymentMode,
} from "@workspace/api-client-react";
import { uploadPaymentProof } from "@/lib/upload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, Upload } from "lucide-react";

/** INR formatter. */
function fmt(n: number) {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

/** Today as YYYY-MM-DD. */
function today() {
  return new Date().toISOString().slice(0, 10);
}

const CYCLE_MODES = ["One_Time", "Recurring", "Non_Recurring"] as const;

// Deposit disposition options per item row.
const DEPOSIT_DISPOSITION_OPTIONS: {
  value: DepositDispositionDisposition;
  label: string;
}[] = [
  { value: "adjust_new_deposit", label: "Adjust → new deposit" },
  { value: "adjust_package", label: "Adjust → package (non-taxable)" },
  { value: "refund", label: "Refund" },
];

interface HeldDepositProp {
  id: number;
  amount: number;
  status: string;
  lockInUntilDate?: string | null;
}

/** State for a single restructure item row. */
interface ItemRow {
  membershipId: number;
  label: string;
  newPerMonth: string;
  quantity: string;
  gstApplicable: boolean;
  newDepositAmount: string;
  depositDisposition: DepositDispositionDisposition;
  /** Explicit Held-deposit id paired to this row (required when disposition ≠ adjust_new_deposit). */
  pairedDepositId: number | null;
  /** Amount of the paired Held deposit (for heldDepositAmount in the payload). */
  pairedDepositAmount: number | null;
}

interface RestructureBundleDialogProps {
  accountId: number;
  /** All active recurring memberships on the account (sourced from useListAccountMemberships in parent). */
  allActiveMemberships: Membership[];
  /** Held security deposits on the account (sourced from useGetAccountDeposits in parent). */
  heldDeposits: HeldDepositProp[];
  periods: PlanSchedulePeriod[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Build a human-readable membership label mirroring account-detail convention. */
function membershipLabel(m: Membership): string {
  // periods may carry planName; fall back to planId then id.
  return m.planId
    ? `Plan #${m.planId}${m.quantity && m.quantity > 1 ? ` ×${m.quantity}` : ""} (mbr #${m.id})`
    : `Plan (mbr #${m.id})`;
}

/** Filter memberships to active recurring (cycleMode not null = Monthly/Quarterly/Six_Month/Yearly). */
function isActiveRecurring(m: Membership): boolean {
  return m.status === "active" && m.cycleMode != null;
}

export function RestructureBundleDialog({
  accountId,
  allActiveMemberships,
  heldDeposits,
  periods,
  open,
  onOpenChange,
}: RestructureBundleDialogProps) {
  const { toast } = useToast();

  // ── Form header state ──────────────────────────────────────────────────────
  const [cutoverDate, setCutoverDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [durationMonths, setDurationMonths] = useState<string>("6");
  const [newCycleMode, setNewCycleMode] = useState<string>("One_Time");
  const [additionalDiscountPct, setAdditionalDiscountPct] = useState<string>("");

  // ── Membership picker ──────────────────────────────────────────────────────
  // Only show active recurring memberships; default all ticked.
  const activeRecurring = allActiveMemberships.filter(isActiveRecurring);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(activeRecurring.map((m) => m.id)),
  );

  function toggleMembership(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // ── Per-item rows ──────────────────────────────────────────────────────────
  // Default: one row per selected active-recurring membership.
  // We rebuild rows when the selection changes (derived from activeRecurring).
  const [itemOverrides, setItemOverrides] = useState<
    Map<number, Partial<ItemRow>>
  >(() => new Map());

  // Default held deposit pre-selection: if only one exists, pre-select it.
  const singleHeldDeposit = heldDeposits.length === 1 ? heldDeposits[0] : null;

  function getItemRow(m: Membership): ItemRow {
    const overrides = itemOverrides.get(m.id) ?? {};
    return {
      membershipId: m.id,
      label: membershipLabel(m),
      newPerMonth:
        overrides.newPerMonth ??
        (m.unitPrice != null ? String(Number(m.unitPrice)) : ""),
      quantity: overrides.quantity ?? (m.quantity != null ? String(m.quantity) : "1"),
      gstApplicable: overrides.gstApplicable ?? (m.gstApplicable ?? false),
      newDepositAmount: overrides.newDepositAmount ?? "",
      depositDisposition: overrides.depositDisposition ?? "adjust_new_deposit",
      pairedDepositId:
        overrides.pairedDepositId !== undefined
          ? overrides.pairedDepositId
          : singleHeldDeposit?.id ?? null,
      pairedDepositAmount:
        overrides.pairedDepositAmount !== undefined
          ? overrides.pairedDepositAmount
          : singleHeldDeposit?.amount ?? null,
    };
  }

  // Selected item rows (ordered by activeRecurring for stable rendering).
  const selectedRows = activeRecurring
    .filter((m) => selectedIds.has(m.id))
    .map(getItemRow);

  function updateItemOverride(membershipId: number, patch: Partial<ItemRow>) {
    setItemOverrides((prev) => {
      const next = new Map(prev);
      next.set(membershipId, { ...(next.get(membershipId) ?? {}), ...patch });
      return next;
    });
  }

  // ── Credit disposition ────────────────────────────────────────────────────
  const [creditDisposition, setCreditDisposition] =
    useState<"adjust" | "refund">("adjust");

  // Paid-gating: determine if the overlap periods for the selected memberships
  // are paid. An overlap period = the current/in-flight period for each selected
  // membership. We consider the overlap unpaid if ANY selected membership's
  // most-recent period is Unpaid or Partial (i.e. not fully Paid).
  const selectedMembershipIds = new Set(selectedRows.map((r) => r.membershipId));
  const relevantPeriods = periods.filter((p) =>
    selectedMembershipIds.has(p.membershipId),
  );
  // "Overlap is paid" = all relevant periods have status === "Paid".
  // If there are no relevant periods yet (e.g. no periods passed), treat as unpaid
  // (safe default prevents offering a refund on uncollected credit).
  const overlapIsPaid =
    relevantPeriods.length > 0 &&
    relevantPeriods.every((p) => p.status === "Paid");

  // creditTotal preview: use amountPaid sum when paid; 0 otherwise.
  // (Exact server-side value is computed from overlap days; this is the best
  // client-side estimate available before submit.)
  const creditTotalForPreview = overlapIsPaid
    ? relevantPeriods.reduce((s, p) => s + (p.amountPaid ?? 0), 0)
    : 0;

  // Force "adjust" if credit is not refundable (paid-gating guard).
  const effectiveCreditDisposition: "adjust" | "refund" =
    !overlapIsPaid && creditDisposition === "refund" ? "adjust" : creditDisposition;

  // ── Refund payment section ─────────────────────────────────────────────────
  const [refundPaymentMode, setRefundPaymentMode] = useState<"Cash" | "UPI">("Cash");
  const [refundPaymentDate, setRefundPaymentDate] = useState<string>(() => today());
  const [refundProofUrl, setRefundProofUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Show the refund payment section if ANY disposition is "refund".
  const anyRefund =
    effectiveCreditDisposition === "refund" ||
    selectedRows.some((r) => r.depositDisposition === "refund");

  async function onProof(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadPaymentProof(file);
      if (r.ok) {
        setRefundProofUrl(r.url);
        toast({ title: "Proof uploaded." });
      } else {
        toast({
          title: "Couldn't upload proof",
          description: r.message || `Upload failed (HTTP ${r.status}).`,
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't upload proof",
        description: (err as Error)?.message ?? "Network error.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }

  // ── Live net-due ──────────────────────────────────────────────────────────
  const dur = parseInt(durationMonths, 10) || 1;

  // New package total from entered per-month rates.
  const newPackageTotal = selectedRows.reduce((sum, row) => {
    const pm = parseFloat(row.newPerMonth) || 0;
    const qty = parseInt(row.quantity, 10) || 1;
    return sum + pm * qty * dur;
  }, 0);

  const discountPctNum = parseFloat(additionalDiscountPct) || 0;
  const discountedPackageTotal = newPackageTotal * (1 - discountPctNum / 100);
  const effectivePackageTotal = discountPctNum > 0 ? discountedPackageTotal : newPackageTotal;

  // Sum of held-deposit amounts where disposition === "adjust_package".
  const depositPackageTotal = selectedRows.reduce((sum, row) => {
    if (row.depositDisposition === "adjust_package" && row.pairedDepositAmount != null) {
      return sum + row.pairedDepositAmount;
    }
    return sum;
  }, 0);

  // Sum of refunded amounts (deposit + credit refund).
  const depositRefundTotal = selectedRows.reduce((sum, row) => {
    if (row.depositDisposition === "refund" && row.pairedDepositAmount != null) {
      return sum + row.pairedDepositAmount;
    }
    return sum;
  }, 0);
  const creditRefundTotal =
    effectiveCreditDisposition === "refund" ? creditTotalForPreview : 0;
  const totalRefundAmount = depositRefundTotal + creditRefundTotal;

  // Net due = new package total (after discount) - credit applied - deposit applied to package.
  const netDue =
    effectivePackageTotal -
    (effectiveCreditDisposition === "adjust" ? creditTotalForPreview : 0) -
    depositPackageTotal;

  // ── Result state ──────────────────────────────────────────────────────────
  const [result, setResult] = useState<RestructureResult | null>(null);

  const restructure = useRestructureBundle();

  // ── Submit ────────────────────────────────────────────────────────────────
  async function onSubmit() {
    if (!dur || dur < 1) {
      toast({ title: "Duration must be at least 1 month.", variant: "destructive" });
      return;
    }
    if (!cutoverDate) {
      toast({ title: "Cutover date is required.", variant: "destructive" });
      return;
    }
    if (selectedRows.length === 0) {
      toast({ title: "Select at least one membership to restructure.", variant: "destructive" });
      return;
    }

    const restructureItems: RestructureItem[] = selectedRows.map((row) => ({
      membershipId: row.membershipId,
      newPerMonth: parseFloat(row.newPerMonth) || 0,
      quantity: parseInt(row.quantity, 10) || 1,
      gstApplicable: row.gstApplicable,
      newDepositAmount:
        row.newDepositAmount.trim() !== "" ? parseFloat(row.newDepositAmount) : null,
    }));

    // Build depositDispositions for rows that need one (refund or adjust_package
    // require an explicit depositId; adjust_new_deposit is implied by absence).
    const depositDispositions: DepositDisposition[] = selectedRows
      .filter((row) => row.depositDisposition !== "adjust_new_deposit")
      .map((row) => ({
        depositId: row.pairedDepositId ?? 0,
        membershipId: row.membershipId,
        disposition: row.depositDisposition,
        amount: row.pairedDepositAmount ?? undefined,
        heldDepositAmount:
          row.depositDisposition === "adjust_package"
            ? (row.pairedDepositAmount ?? null)
            : null,
      }));

    const discountPct =
      additionalDiscountPct.trim() !== "" ? parseFloat(additionalDiscountPct) : null;

    try {
      const r = await restructure.mutateAsync({
        id: accountId,
        data: {
          membershipIds: selectedRows.map((row) => row.membershipId),
          cutoverDate,
          newCycleMode,
          durationMonths: dur,
          items: restructureItems,
          additionalDiscountPct: discountPct,
          depositDispositions: depositDispositions.length > 0 ? depositDispositions : null,
          creditDisposition: effectiveCreditDisposition as RestructureBodyCreditDisposition,
          ...(anyRefund
            ? {
                refundPaymentMode: refundPaymentMode as RestructureBodyRefundPaymentMode,
                refundPaymentDate,
                refundProofUrl: refundProofUrl ?? null,
              }
            : {}),
        },
      });
      setResult(r);
      toast({ title: `Bundle restructured — Credit Note ${r.creditNoteNumber} issued.` });
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "";
      toast({
        title: "Restructure failed.",
        description: msg || "Please check the inputs and try again.",
        variant: "destructive",
      });
    }
  }

  // ── Reset on close ────────────────────────────────────────────────────────
  function handleClose(v: boolean) {
    if (!v) {
      setResult(null);
      setCutoverDate(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
      });
      setDurationMonths("6");
      setNewCycleMode("One_Time");
      setAdditionalDiscountPct("");
      setSelectedIds(new Set(activeRecurring.map((m) => m.id)));
      setItemOverrides(new Map());
      setCreditDisposition("adjust");
      setRefundPaymentMode("Cash");
      setRefundPaymentDate(today());
      setRefundProofUrl(null);
    }
    onOpenChange(v);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Restructure Bundle</DialogTitle>
          <DialogDescription>
            Choose which memberships to restructure, set deposit and credit dispositions,
            then enter new plan terms. The server computes the exact credit note from
            overlap days.
          </DialogDescription>
        </DialogHeader>

        {/* ── Success result panel ──────────────────────────────────────────── */}
        {result && (
          <div className="rounded-md border border-green-200 bg-green-50/60 p-4 space-y-3">
            <div className="flex items-center gap-2 text-green-800 font-medium">
              <CheckCircle2 className="h-5 w-5" />
              Restructure complete
            </div>

            {/* Display-only — plain markup, no FormField ancestor. */}
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div className="rounded-md border bg-background px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Credit note</p>
                <p className="font-mono font-semibold">{result.creditNoteNumber}</p>
              </div>
              <div className="rounded-md border bg-background px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Effective credit</p>
                <p className="font-semibold tabular-nums text-amber-600">
                  {fmt(result.effectiveCreditTotal ?? result.creditNoteTotal)}
                </p>
              </div>
              {(result.depositAdjustToPackageTotal ?? 0) > 0 && (
                <div className="rounded-md border bg-background px-3 py-2">
                  <p className="text-[11px] text-muted-foreground">Deposit → package</p>
                  <p className="font-semibold tabular-nums text-blue-600">
                    {fmt(result.depositAdjustToPackageTotal ?? 0)}
                  </p>
                </div>
              )}
              {(result.refundIds?.length ?? 0) > 0 && (
                <div className="rounded-md border bg-background px-3 py-2">
                  <p className="text-[11px] text-muted-foreground">Refunds issued</p>
                  <p className="font-semibold tabular-nums text-orange-600">
                    {result.refundIds?.length} refund{result.refundIds!.length !== 1 ? "s" : ""}
                  </p>
                </div>
              )}
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                <p className="text-[11px] text-muted-foreground">NET DUE</p>
                <p className="text-lg font-bold tabular-nums">{fmt(result.netDue)}</p>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">
              New quote group: <span className="font-mono">{result.newQuoteGroupId}</span> ·{" "}
              {result.newMembershipIds.length} new membership
              {result.newMembershipIds.length !== 1 ? "s" : ""} created.
            </p>
          </div>
        )}

        {/* ── Form ──────────────────────────────────────────────────────────── */}
        {!result && (
          <div className="space-y-6">
            {/* ── Step 1: Header (cutover date, duration, cycle mode) ──────── */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Cutover date</Label>
                <Input
                  type="date"
                  value={cutoverDate}
                  onChange={(e) => setCutoverDate(e.target.value)}
                  className="h-9"
                />
                <p className="text-[11px] text-muted-foreground">
                  New memberships start on this date; old ones end the day before.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Duration (months)</Label>
                <Input
                  type="number"
                  min={1}
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">New cycle mode</Label>
                <Select value={newCycleMode} onValueChange={setNewCycleMode}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CYCLE_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Step 2: Membership picker ─────────────────────────────────── */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Memberships to restructure
              </p>
              {activeRecurring.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No active recurring memberships found on this account.
                </p>
              ) : (
                <div className="rounded-md border divide-y">
                  {activeRecurring.map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={selectedIds.has(m.id)}
                        onCheckedChange={() => toggleMembership(m.id)}
                      />
                      <span className="text-sm">
                        {membershipLabel(m)}
                        {m.cycleMode && (
                          <span className="ml-2 text-[11px] text-muted-foreground">
                            {m.cycleMode.replace(/_/g, " ")}
                          </span>
                        )}
                        {m.unitPrice != null && (
                          <span className="ml-2 text-[11px] text-muted-foreground">
                            {fmt(Number(m.unitPrice))}/mo
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* ── Step 3: Credit disposition (top-level) ────────────────────── */}
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Credit note disposition
              </p>
              <div className="flex items-center gap-3">
                <Select
                  value={effectiveCreditDisposition}
                  onValueChange={(v) => setCreditDisposition(v as "adjust" | "refund")}
                >
                  <SelectTrigger className="h-9 w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="adjust">Adjust → new plan</SelectItem>
                    <SelectItem value="refund" disabled={!overlapIsPaid}>
                      Refund
                    </SelectItem>
                  </SelectContent>
                </Select>
                {!overlapIsPaid && (
                  <p className="text-[11px] text-muted-foreground">
                    No paid overlap to refund.
                    {creditTotalForPreview === 0
                      ? " Credit applied: ₹0."
                      : ""}
                  </p>
                )}
              </div>
            </div>

            {/* ── Step 4: Per-item table ─────────────────────────────────────── */}
            {selectedRows.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Items ({selectedRows.length})
                </p>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Membership</TableHead>
                        <TableHead className="text-right w-28">Per month (₹)</TableHead>
                        <TableHead className="text-right w-16">Qty</TableHead>
                        <TableHead className="w-16">GST</TableHead>
                        <TableHead className="text-right w-32">New deposit (₹)</TableHead>
                        <TableHead className="w-48">Deposit disposition</TableHead>
                        <TableHead className="w-52">Held deposit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedRows.map((row) => {
                        const needsHeldPick =
                          row.depositDisposition === "refund" ||
                          row.depositDisposition === "adjust_package";
                        return (
                          <TableRow key={row.membershipId}>
                            <TableCell className="text-sm font-medium">
                              {row.label}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min={0}
                                value={row.newPerMonth}
                                onChange={(e) =>
                                  updateItemOverride(row.membershipId, {
                                    newPerMonth: e.target.value,
                                  })
                                }
                                onFocus={(e) => e.target.select()}
                                className="h-8 text-right w-full"
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min={1}
                                value={row.quantity}
                                onChange={(e) =>
                                  updateItemOverride(row.membershipId, {
                                    quantity: e.target.value,
                                  })
                                }
                                onFocus={(e) => e.target.select()}
                                className="h-8 text-right w-full"
                                placeholder="1"
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={row.gstApplicable}
                                  onCheckedChange={(v) =>
                                    updateItemOverride(row.membershipId, {
                                      gstApplicable: Boolean(v),
                                    })
                                  }
                                  aria-label="GST applicable"
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min={0}
                                value={row.newDepositAmount}
                                onChange={(e) =>
                                  updateItemOverride(row.membershipId, {
                                    newDepositAmount: e.target.value,
                                  })
                                }
                                onFocus={(e) => e.target.select()}
                                className="h-8 text-right w-full"
                                placeholder="—"
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={row.depositDisposition}
                                onValueChange={(v) =>
                                  updateItemOverride(row.membershipId, {
                                    depositDisposition: v as DepositDispositionDisposition,
                                  })
                                }
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DEPOSIT_DISPOSITION_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {needsHeldPick ? (
                                heldDeposits.length === 0 ? (
                                  <p className="text-[11px] text-muted-foreground">
                                    No Held deposits found.
                                  </p>
                                ) : (
                                  <Select
                                    value={row.pairedDepositId != null ? String(row.pairedDepositId) : ""}
                                    onValueChange={(v) => {
                                      const dep = heldDeposits.find(
                                        (d) => d.id === Number(v),
                                      );
                                      updateItemOverride(row.membershipId, {
                                        pairedDepositId: Number(v),
                                        pairedDepositAmount: dep?.amount ?? null,
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Pick held deposit" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {heldDeposits.map((dep) => (
                                        <SelectItem key={dep.id} value={String(dep.id)}>
                                          {fmt(dep.amount)}
                                          {dep.lockInUntilDate
                                            ? ` · held until ${dep.lockInUntilDate}`
                                            : ""}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )
                              ) : (
                                <p className="text-[11px] text-muted-foreground px-1">
                                  —
                                </p>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* ── Additional discount ───────────────────────────────────────── */}
            <div className="flex items-end gap-4">
              <div className="w-48 space-y-1">
                <Label className="text-xs">Additional discount (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={additionalDiscountPct}
                  onChange={(e) => setAdditionalDiscountPct(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="h-9"
                  placeholder="0"
                />
              </div>

              {/* Display-only live preview — plain markup, no FormField. */}
              <div className="flex-1 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                <div className="rounded-md border bg-muted/30 px-2 py-1.5">
                  <p className="text-[11px] text-muted-foreground">Package subtotal</p>
                  <p className="font-semibold tabular-nums">{fmt(newPackageTotal)}</p>
                </div>
                {creditTotalForPreview > 0 && (
                  <div className="rounded-md border bg-amber-50 px-2 py-1.5">
                    <p className="text-[11px] text-muted-foreground">
                      Credit{" "}
                      {effectiveCreditDisposition === "adjust" ? "(applied)" : "(refund)"}
                    </p>
                    <p className="font-semibold tabular-nums text-amber-600">
                      {effectiveCreditDisposition === "adjust"
                        ? `−${fmt(creditTotalForPreview)}`
                        : fmt(creditTotalForPreview)}
                    </p>
                  </div>
                )}
                {depositPackageTotal > 0 && (
                  <div className="rounded-md border bg-blue-50 px-2 py-1.5">
                    <p className="text-[11px] text-muted-foreground">
                      Deposit → pkg (non-taxable)
                    </p>
                    <p className="font-semibold tabular-nums text-blue-600">
                      −{fmt(depositPackageTotal)}
                    </p>
                  </div>
                )}
                {totalRefundAmount > 0 && (
                  <div className="rounded-md border bg-orange-50 px-2 py-1.5">
                    <p className="text-[11px] text-muted-foreground">Refund (cash out)</p>
                    <p className="font-semibold tabular-nums text-orange-600">
                      {fmt(totalRefundAmount)}
                    </p>
                  </div>
                )}
                <div className="rounded-md border border-primary/20 bg-primary/5 px-2 py-1.5">
                  <p className="text-[11px] text-muted-foreground">EST. NET DUE</p>
                  <p className="text-base font-bold tabular-nums">{fmt(Math.max(0, netDue))}</p>
                </div>
              </div>
            </div>

            {/* ── Refund payment section (shown only when any refund is selected) ── */}
            {anyRefund && (
              <div className="rounded-md border border-orange-200 bg-orange-50/40 p-4 space-y-3">
                <p className="text-xs font-medium text-orange-800 uppercase tracking-wide">
                  Refund payment details
                </p>
                <p className="text-[11px] text-muted-foreground">
                  A formal refund record will be created. Specify how the cash is returned.
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Payment mode</Label>
                    <Select
                      value={refundPaymentMode}
                      onValueChange={(v) => setRefundPaymentMode(v as "Cash" | "UPI")}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Refund date</Label>
                    <Input
                      type="date"
                      value={refundPaymentDate}
                      onChange={(e) => setRefundPaymentDate(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  {refundPaymentMode === "UPI" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Proof of payment (optional)</Label>
                      <div className="flex items-center gap-2">
                        <Button asChild variant="outline" size="sm" disabled={uploading}>
                          <label className="cursor-pointer">
                            {uploading ? (
                              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="mr-1.5 h-4 w-4" />
                            )}
                            {refundProofUrl ? "Replace" : "Upload"}
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              className="hidden"
                              onChange={(e) => onProof(e.target.files?.[0])}
                            />
                          </label>
                        </Button>
                        {refundProofUrl && (
                          <a
                            href={refundProofUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary underline"
                          >
                            View proof
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground">
              The credit note total is computed server-side from actual overlap days.
              Deposits not set to "Adjust → package" or "Refund" will be rolled into
              the new memberships at the entered amount.
            </p>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {!result && (
            <Button
              size="sm"
              disabled={restructure.isPending || selectedRows.length === 0}
              onClick={onSubmit}
            >
              {restructure.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              {restructure.isPending ? "Restructuring…" : "Restructure Bundle"}
            </Button>
          )}
          <Button
            size="sm"
            variant={result ? "default" : "outline"}
            onClick={() => handleClose(false)}
          >
            {result ? "Done" : "Cancel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
